// MarketsTab — Tab 1 der Hyperliquid-Page.
// Layout:
//   1. Marquee — Cross-Venue Funding (via useHlPredictedFundings)
//   2. Marquee — Whale Strip (via useWhaleAlertsStore, gefüllt im AppShell)
//   3. 3-Spalten: Funding 48h Chart · L2 Order Book · Live Trades Tape
//
// EVE-Zugang: globaler `EvePresenceDrawer` (app-shell), kein eigener Composer
// pro Sub-Tab — Doc-Konvention „EVE ist allgegenwärtig" (Phase 4Q).
//
// Alle 4 Live-Quellen sind echte HL-Hooks (Phase 4G):
//   - useHlPredictedFundings (60s poll predictedFundings)
//   - useHlFundingHistory  (30s poll fundingHistory, Recharts)
//   - useHlOrderBook       (1s poll l2Book)
//   - useHlRecentTrades    (4s poll recentTrades)

import { useMemo } from 'react';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Marquee } from '@/components/ui/marquee';
import { useHlFundingHistory } from '@/hooks/use-hl-funding-history';
import { useHlOrderBook } from '@/hooks/use-hl-order-book';
import { useHlPredictedFundings } from '@/hooks/use-hl-predicted-fundings';
import { useHlRecentTrades } from '@/hooks/use-hl-recent-trades';
import type {
  FundingHistoryEntry,
  L2Level,
  RecentTrade,
} from '@/lib/hl-info-api';
import { selectWhaleAlerts, useWhaleAlertsStore } from '@/stores/whale-alerts-store';
import type { WhaleAlert } from '@/types/hyperliquid';
import type { HyperliquidCoin } from './hyperliquid-metrics-bar';

const CROSS_VENUE_ORDER = ['HlPerp', 'BinPerp', 'BybitPerp', 'OkxPerp', 'DydxPerp', 'VertexPerp'];
const CROSS_VENUE_LABELS: Record<string, string> = {
  HlPerp: 'Hyperliquid',
  BinPerp: 'Binance',
  BybitPerp: 'Bybit',
  OkxPerp: 'OKX',
  DydxPerp: 'dYdX',
  VertexPerp: 'Vertex',
};

interface MarketsTabProps {
  coin: HyperliquidCoin;
  /** WS-Live Funding (Decimal). Wird als Fallback gezeigt falls predictedFundings keine HL-Rate hat. */
  hlFunding?: number | null;
}

export function MarketsTab({ coin, hlFunding = null }: MarketsTabProps) {
  const whaleAlerts = useWhaleAlertsStore(selectWhaleAlerts);
  const { byCoinVenue } = useHlPredictedFundings();
  const venueMap = byCoinVenue.get(coin);

  return (
    <div className="space-y-3">
      <Marquee
        className="glass rounded-xl py-2 text-sm"
        durationSec={35}
        items={[
          <span key="hdr" className="flex shrink-0 items-center gap-1.5 px-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              Predicted FR · {coin}
            </span>
          </span>,
          ...CROSS_VENUE_ORDER.map((venueKey) => {
            const label = CROSS_VENUE_LABELS[venueKey] ?? venueKey;
            // HL-Rate: bevorzugt aus predictedFundings, fallback auf hlFunding-Prop.
            let funding = venueMap?.get(venueKey) ?? null;
            if (venueKey === 'HlPerp' && funding === null) funding = hlFunding;
            return (
              <span key={venueKey} className="flex shrink-0 items-center gap-1.5">
                <span className="text-neutral-500">{label}</span>
                <FundingValue value={funding} />
              </span>
            );
          }),
        ]}
      />

      <WhaleStrip alerts={whaleAlerts} />

      {/* 3-Spalten: Funding-Chart + OrderBook + Live-Trades */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3" style={{ minHeight: 460 }}>
        <FundingChartPanel coin={coin} />
        <OrderBookPanel coin={coin} />
        <LiveTradesPanel coin={coin} />
      </div>
    </div>
  );
}

// ── Cross-Venue Marquee Helpers ──────────────────────────────────────────────

function FundingValue({ value }: { value: number | null }) {
  if (value === null || !Number.isFinite(value)) {
    return <span className="font-bold tabular text-neutral-600">—</span>;
  }
  // predictedFundings + WS-assetCtx liefern beide Decimal (z.B. 0.0000089). ×100 für %.
  const pct = value * 100;
  return (
    <span
      className={`font-bold tabular ${pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
    >
      {pct >= 0 ? '+' : '−'}
      {Math.abs(pct).toFixed(4)}%
    </span>
  );
}

// ── Whale-Strip ──────────────────────────────────────────────────────────────

function WhaleStrip({ alerts }: { alerts: ReadonlyArray<WhaleAlert> }) {
  if (alerts.length === 0) {
    return (
      <div className="glass rounded-xl px-4 py-2 text-center text-[12px] text-neutral-500">
        🐋 Noch keine Whale-Trades seit dem Login (≥ $1M). WS lauscht…
      </div>
    );
  }
  return (
    <Marquee
      durationSec={50}
      items={alerts.map((w) => (
        <div
          key={`${w.hash}-${w.time}`}
          className={`glass flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm ${
            w.side === 'BUY'
              ? 'border-emerald-500/30 bg-emerald-500/5'
              : 'border-rose-500/30 bg-rose-500/5'
          }`}
        >
          <span className="text-lg">🐋</span>
          <span
            className={`font-bold ${w.side === 'BUY' ? 'text-emerald-400' : 'text-rose-400'}`}
          >
            {w.side}
          </span>
          <span className="font-bold">{w.coin}</span>
          <span className="font-bold text-amber-400">{formatUsdShort(w.sizeUsd)}</span>
          <span className="text-neutral-500">@${formatPrice(w.price)}</span>
          <span className="text-neutral-600">{formatAge(w.time)}</span>
          {w.wallet ? (
            <button
              type="button"
              className="font-mono text-[11px] text-neutral-500 hover:text-amber-400"
              title={`Wallet ${w.wallet}`}
            >
              {shortAddr(w.wallet)}
            </button>
          ) : null}
        </div>
      ))}
    />
  );
}

// ── Funding-Chart-Panel (Recharts) ───────────────────────────────────────────

function FundingChartPanel({ coin }: { coin: HyperliquidCoin }) {
  const { entries, averageFunding, positiveCount, totalCount, loading } =
    useHlFundingHistory(coin);

  const chartData = useMemo(
    () =>
      entries.map((e: FundingHistoryEntry) => ({
        time: e.time,
        funding: e.fundingRate * 100, // → % für Y-Axis-Lesbarkeit
        premium: e.premium * 100,
      })),
    [entries],
  );

  const avgPct = averageFunding !== null ? averageFunding * 100 : null;
  const avgSign = avgPct === null ? '' : avgPct >= 0 ? '+' : '−';
  const avgFormatted =
    avgPct === null ? '—' : `${avgSign}${Math.abs(avgPct).toFixed(4)}%`;
  const avgTone = avgPct === null ? 'text-neutral-500' : avgPct >= 0 ? 'text-emerald-400' : 'text-rose-400';

  return (
    <div className="glass-strong flex flex-col overflow-hidden rounded-xl">
      <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
          📈 Funding History 48h · {coin}
        </span>
        <div className="flex gap-2 text-[11px]">
          <span className={avgTone}>Ø {avgFormatted}</span>
          {totalCount > 0 ? (
            <span className="text-neutral-500">
              {positiveCount}/{totalCount} positiv
            </span>
          ) : null}
        </div>
      </div>
      <div className="relative flex-1">
        {loading && chartData.length === 0 ? (
          <div className="grid h-full place-items-center text-[11px] text-neutral-500">
            Lädt Funding-Historie…
          </div>
        ) : chartData.length === 0 ? (
          <div className="grid h-full place-items-center text-[11px] text-neutral-500">
            Keine Daten für {coin}.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="time"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(t: number) => formatHour(t)}
                tick={{ fill: '#737373', fontSize: 10 }}
                stroke="#404040"
              />
              <YAxis
                tick={{ fill: '#737373', fontSize: 10 }}
                stroke="#404040"
                tickFormatter={(v: number) => `${v.toFixed(3)}%`}
                width={60}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgba(15,15,18,0.95)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelFormatter={(t: number) => new Date(t).toLocaleString('de-DE')}
                formatter={(value: number, name: string) => [
                  `${value.toFixed(4)}%`,
                  name === 'funding' ? 'Funding' : 'Premium',
                ]}
              />
              <Line
                type="monotone"
                dataKey="funding"
                stroke="#fbbf24"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="premium"
                stroke="#818cf8"
                strokeWidth={1}
                dot={false}
                isAnimationActive={false}
                strokeOpacity={0.6}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ── Order-Book-Panel ─────────────────────────────────────────────────────────

function OrderBookPanel({ coin }: { coin: HyperliquidCoin }) {
  const { bids, asks, midPrice, imbalance, loading } = useHlOrderBook(coin, { depth: 8 });

  const maxSize = useMemo(() => {
    const all = [...bids, ...asks].map((l) => l.size);
    return all.length > 0 ? Math.max(...all) : 0;
  }, [bids, asks]);

  const bidPct = imbalance !== null ? Math.round(imbalance * 100) : null;
  const askPct = imbalance !== null ? 100 - (bidPct ?? 50) : null;

  return (
    <div className="glass-strong flex flex-col overflow-hidden rounded-xl">
      <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
          📖 Order Book · {coin}
        </span>
        <div className="flex items-center gap-2 text-[11px]">
          {bidPct !== null ? (
            <>
              <span className="text-emerald-400">{bidPct}% BID</span>
              <span
                className={`font-bold ${bidPct >= 50 ? 'text-emerald-400' : 'text-rose-400'}`}
              >
                {bidPct >= 50 ? '▲' : '▼'}
              </span>
              <span className="text-rose-400">{askPct}% ASK</span>
            </>
          ) : (
            <span className="text-neutral-500">—</span>
          )}
        </div>
      </div>
      {bidPct !== null ? (
        <div className="flex h-1.5">
          <div className="bg-emerald-500/50" style={{ width: `${bidPct}%` }} />
          <div className="flex-1 bg-rose-500/50" />
        </div>
      ) : null}
      {loading && bids.length === 0 && asks.length === 0 ? (
        <div className="grid flex-1 place-items-center text-[11px] text-neutral-500">
          Lädt Order Book…
        </div>
      ) : bids.length === 0 && asks.length === 0 ? (
        <div className="grid flex-1 place-items-center text-[11px] text-neutral-500">
          Keine Order-Book-Daten für {coin}.
        </div>
      ) : (
        <div className="flex flex-1 flex-col text-[12px] tabular">
          <div className="flex flex-1 flex-col-reverse">
            {asks.map((a) => (
              <OrderBookRow key={`a-${a.price}`} level={a} maxSize={maxSize} side="ask" />
            ))}
          </div>
          {midPrice !== null ? (
            <div className="bg-neutral-800/60 px-3 py-1 text-center">
              <span className="text-sm font-black text-white">${formatPrice(midPrice)}</span>
            </div>
          ) : null}
          <div className="flex-1">
            {bids.map((b) => (
              <OrderBookRow key={`b-${b.price}`} level={b} maxSize={maxSize} side="bid" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OrderBookRow({
  level,
  maxSize,
  side,
}: {
  level: L2Level;
  maxSize: number;
  side: 'bid' | 'ask';
}) {
  const pct = maxSize > 0 ? (level.size / maxSize) * 100 : 0;
  const bg = side === 'bid' ? 'bg-emerald-500/10' : 'bg-rose-500/10';
  const fg = side === 'bid' ? 'text-emerald-400' : 'text-rose-400';
  return (
    <div className="relative flex justify-between px-3 py-0.5">
      <div className={`absolute inset-0 ${bg}`} style={{ width: `${pct}%` }} />
      <span className={`z-10 font-mono ${fg}`}>{formatPrice(level.price)}</span>
      <span className="z-10 text-neutral-500">{level.size.toFixed(3)}</span>
    </div>
  );
}

// ── Live-Trades-Panel ────────────────────────────────────────────────────────

function LiveTradesPanel({ coin }: { coin: HyperliquidCoin }) {
  const { trades, loading } = useHlRecentTrades(coin);

  return (
    <div className="glass-strong flex flex-col overflow-hidden rounded-xl">
      <div className="border-b border-white/5 px-3 py-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
          ⚡ Live Trades · {coin}
        </span>
      </div>
      <div className="grid grid-cols-4 px-3 py-1.5 text-[10px] uppercase text-neutral-600">
        <span>Side</span>
        <span className="text-right">Price</span>
        <span className="text-right">Size</span>
        <span className="text-right">USD</span>
      </div>
      <div className="scroll-hidden flex-1 overflow-y-auto text-[12px] tabular">
        {loading && trades.length === 0 ? (
          <div className="grid h-full place-items-center text-[11px] text-neutral-500">
            Lädt Trades…
          </div>
        ) : trades.length === 0 ? (
          <div className="grid h-full place-items-center text-[11px] text-neutral-500">
            Keine Trades für {coin}.
          </div>
        ) : (
          trades.map((t: RecentTrade, i) => {
            const isBuy = t.side === 'B';
            const isBig = t.usd >= 250_000;
            return (
              <div
                key={`${t.hash}-${i}`}
                className={`grid grid-cols-4 border-b border-white/5 px-3 py-1.5 ${
                  isBig ? 'bg-amber-500/5' : ''
                }`}
              >
                <span
                  className={`font-bold ${isBuy ? 'text-emerald-400' : 'text-rose-400'}`}
                >
                  {isBuy ? 'BUY' : 'SELL'}
                </span>
                <span
                  className={`text-right font-mono ${
                    isBuy ? 'text-emerald-400/80' : 'text-rose-400/80'
                  }`}
                >
                  {formatPrice(t.price)}
                </span>
                <span className="text-right text-neutral-400">{t.size.toFixed(3)}</span>
                <span
                  className={`text-right font-mono ${
                    isBig ? 'text-amber-400' : 'text-neutral-500'
                  }`}
                >
                  {formatUsdShort(t.usd)}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Helper-Formatters ────────────────────────────────────────────────────────

function formatUsdShort(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPrice(value: number): string {
  if (value >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return value.toFixed(2);
}

function formatHour(timeMs: number): string {
  const d = new Date(timeMs);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatAge(timeMs: number): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - timeMs) / 1000));
  if (diffSec < 60) return `${diffSec}s`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

function shortAddr(addr: string): string {
  if (addr.length < 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-2)}`;
}
